var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Layout, TextLink } from "@stellar/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams, } from "react-router-dom";
import { fetchTransaction } from "helpers/fetchTransaction";
import { readSep24Callbacks, storeSep24Callbacks, } from "helpers/sep24Callbacks";
import { Kyc } from "pages/Kyc";
import { NotFound } from "pages/NotFound";
import { Status } from "pages/Status";
import { Welcome } from "pages/Welcome";
import { Partners } from "components/Partners";
import { capture } from "lib/analytics";
import { ROZO_EVENTS } from "lib/analytics/events";
import { getAmountInAsset, getFeeMode, getIsFeeModeLocked, getRequestedAmount, getUserAccount, } from "types";
import { TxnInfo } from "./pages/TxnInfo";
import "./styles.scss";
export var BUSINESS_SERVER_ENDPOINT = process.env.BUSINESS_SERVER_ENDPOINT;
var ProtectedRoute = function (_a) {
    var children = _a.children, hasSessionToken = _a.hasSessionToken;
    return hasSessionToken ? children : _jsx(Navigate, { to: { pathname: "/", search: "" } });
};
export var App = function () {
    var _a, _b, _c, _d;
    var navigate = useNavigate();
    var location = useLocation();
    var _e = useState(""), token = _e[0], setToken = _e[1];
    var _f = useState(""), sessionToken = _f[0], setSessionToken = _f[1];
    var _g = useState(), txnInfo = _g[0], setTxnInfo = _g[1];
    var searchParams = useSearchParams()[0];
    var _h = useState(false), isDarkMode = _h[0], setIsDarkMode = _h[1];
    var _j = useState(false), showCloseMessage = _j[0], setShowCloseMessage = _j[1];
    var _k = useState(""), bootstrapError = _k[0], setBootstrapError = _k[1];
    var _l = useState(null), setWalletReturnUrl = _l[1];
    var lastSessionBootstrapKey = useRef("");
    var lastNotifiedStatusKeyRef = useRef("");
    var effectiveSessionToken = useMemo(function () {
        var _a;
        if (sessionToken) {
            return sessionToken;
        }
        return (_a = new URLSearchParams(location.search).get("session_token")) !== null && _a !== void 0 ? _a : "";
    }, [location.search, sessionToken]);
    var rozoLogo = useMemo(function () {
        return isDarkMode
            ? "https://bridge.rozo.ai/rozo-white-transparent.png"
            : "https://bridge.rozo.ai/rozo-transparent.png";
    }, [isDarkMode]);
    var headerTitle = useMemo(function () {
        var _a;
        var kind = (_a = txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction) === null || _a === void 0 ? void 0 : _a.kind;
        if (kind === "deposit") {
            return "Deposit Information";
        }
        if (kind === "withdrawal") {
            return "Withdrawal Information";
        }
        return null;
    }, [(_a = txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction) === null || _a === void 0 ? void 0 : _a.kind]);
    // Page view tracking
    useEffect(function () {
        var _a;
        capture(ROZO_EVENTS.PAGE_VIEWED, {
            path: location.pathname,
            transaction_kind: (_a = txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction) === null || _a === void 0 ? void 0 : _a.kind,
        });
    }, [location.pathname]);
    // Force light mode on initial load, ignoring system preference
    useEffect(function () {
        // Remove dark-mode class and add light-mode class to force light mode
        document.body.classList.remove("dark-mode");
        document.body.classList.add("light-mode");
        setIsDarkMode(false);
    }, []);
    // Set token or session token in state
    useEffect(function () {
        setQueryParam();
    }, [location.search]);
    // Get session token from token
    useEffect(function () {
        var hasSessionTokenInQuery = Boolean(new URLSearchParams(location.search).get("session_token"));
        if (token &&
            !location.pathname.includes("txn") &&
            !hasSessionTokenInQuery) {
            var callbackQuery = new URLSearchParams(location.search).get("callback");
            void handleStartSession(token, callbackQuery, true).catch(function (error) {
                var message = error instanceof Error
                    ? error.message
                    : "Failed to initialize transaction session";
                setBootstrapError(message);
            });
        }
    }, [token, location.pathname, location.search]);
    // Reuse prefetched transaction data from /txn to avoid duplicate fetch.
    useEffect(function () {
        var state = location.state;
        if (!(state === null || state === void 0 ? void 0 : state.prefetchedTransaction) || txnInfo) {
            return;
        }
        setTxnInfo({
            transaction: state.prefetchedTransaction,
        });
    }, [location.state, txnInfo]);
    // Set session token if page refreshed
    useEffect(function () {
        if (!effectiveSessionToken ||
            location.pathname.includes("txn") ||
            txnInfo) {
            return;
        }
        if (location.pathname.includes("status"))
            return;
        var callbackQuery = new URLSearchParams(location.search).get("callback");
        handleStartSession(effectiveSessionToken, callbackQuery);
    }, [effectiveSessionToken, location.pathname, location.search, txnInfo]);
    var setQueryParam = function () {
        var queryParams = new URLSearchParams(location.search);
        var token = queryParams.get("token");
        var sessionToken = queryParams.get("session_token");
        if (token) {
            setToken(token);
            setSessionToken("");
        }
        else if (sessionToken) {
            setToken("");
            setSessionToken(sessionToken);
        }
        else {
            // TODO: handle error
        }
    };
    var handleStartSession = function (sToken, callbackQuery, fromAuthToken) { return __awaiter(void 0, void 0, void 0, function () {
        var bootstrapKey, resolvedSessionToken, queryParams, queryOnChangeCallback, _a, storedCallback, storedOnChange, effectiveCallback, effectiveOnChangeCallback, requestBody, response, sessionId, txnInfo_1, transactionId, status, params, error_1;
        var _b, _c, _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    bootstrapKey = "".concat(sToken, "|").concat(callbackQuery !== null && callbackQuery !== void 0 ? callbackQuery : "", "|").concat(Boolean(fromAuthToken));
                    if (lastSessionBootstrapKey.current === bootstrapKey) {
                        return [2 /*return*/];
                    }
                    _f.label = 1;
                case 1:
                    _f.trys.push([1, 6, , 7]);
                    resolvedSessionToken = sToken;
                    queryParams = new URLSearchParams(location.search);
                    queryOnChangeCallback = queryParams.get("on_change_callback");
                    _a = readSep24Callbacks({
                        sessionToken: sToken,
                        authToken: fromAuthToken ? sToken : null,
                    }), storedCallback = _a.callback, storedOnChange = _a.onChangeCallback;
                    effectiveCallback = (_b = callbackQuery !== null && callbackQuery !== void 0 ? callbackQuery : storedCallback) !== null && _b !== void 0 ? _b : null;
                    effectiveOnChangeCallback = queryOnChangeCallback !== null && queryOnChangeCallback !== void 0 ? queryOnChangeCallback : storedOnChange;
                    if (!fromAuthToken) return [3 /*break*/, 4];
                    requestBody = {};
                    // SEP-24: callback can be the literal "postMessage" — that's handled
                    // by the FE via window.postMessage and must NOT be forwarded to backend.
                    if (effectiveOnChangeCallback &&
                        effectiveOnChangeCallback !== "postMessage") {
                        requestBody.on_change_callback = effectiveOnChangeCallback;
                    }
                    return [4 /*yield*/, fetch("".concat(BUSINESS_SERVER_ENDPOINT, "/start"), {
                            method: "POST",
                            headers: {
                                Authorization: "Bearer ".concat(sToken),
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(requestBody),
                        })];
                case 2:
                    response = _f.sent();
                    if (!response.ok) {
                        throw new Error("Start failed: ".concat(response.status, " ").concat(response.statusText));
                    }
                    return [4 /*yield*/, response.json()];
                case 3:
                    sessionId = (_f.sent()).sessionId;
                    resolvedSessionToken = String(sessionId);
                    _f.label = 4;
                case 4:
                    setSessionToken(resolvedSessionToken);
                    return [4 /*yield*/, fetchTransaction(resolvedSessionToken)];
                case 5:
                    txnInfo_1 = _f.sent();
                    setTxnInfo(txnInfo_1);
                    transactionId = (_c = txnInfo_1 === null || txnInfo_1 === void 0 ? void 0 : txnInfo_1.transaction) === null || _c === void 0 ? void 0 : _c.id;
                    storeSep24Callbacks({
                        transactionId: transactionId,
                        sessionToken: resolvedSessionToken,
                        authToken: fromAuthToken ? sToken : null,
                    }, {
                        callback: effectiveCallback !== null && effectiveCallback !== void 0 ? effectiveCallback : undefined,
                        onChangeCallback: effectiveOnChangeCallback !== null && effectiveOnChangeCallback !== void 0 ? effectiveOnChangeCallback : undefined,
                    });
                    setBootstrapError("");
                    lastSessionBootstrapKey.current = bootstrapKey;
                    status = (_d = txnInfo_1 === null || txnInfo_1 === void 0 ? void 0 : txnInfo_1.transaction) === null || _d === void 0 ? void 0 : _d.status;
                    capture(ROZO_EVENTS.PAYMENT_FLOW_STARTED, {
                        transaction_kind: (_e = txnInfo_1 === null || txnInfo_1 === void 0 ? void 0 : txnInfo_1.transaction) === null || _e === void 0 ? void 0 : _e.kind,
                        transaction_id: transactionId,
                        transaction_status: status,
                        has_callback: Boolean(effectiveCallback),
                        on_change_callback_type: !effectiveOnChangeCallback
                            ? "none"
                            : effectiveOnChangeCallback === "postMessage"
                                ? "postMessage"
                                : "url",
                    });
                    params = new URLSearchParams();
                    params.set("session_token", resolvedSessionToken);
                    if (effectiveCallback) {
                        params.set("callback", effectiveCallback);
                    }
                    if (status === "incomplete") {
                        navigate({
                            pathname: "/kyc",
                            search: "?".concat(params.toString()),
                        });
                    }
                    else {
                        navigate({
                            pathname: "/status",
                            search: "?".concat(params.toString()),
                        });
                    }
                    return [3 /*break*/, 7];
                case 6:
                    error_1 = _f.sent();
                    if (lastSessionBootstrapKey.current === bootstrapKey) {
                        lastSessionBootstrapKey.current = "";
                    }
                    capture(ROZO_EVENTS.ERROR_OCCURRED, {
                        error_context: "session_bootstrap",
                        error_message: error_1 instanceof Error ? error_1.message : String(error_1),
                    });
                    throw error_1;
                case 7: return [2 /*return*/];
            }
        });
    }); };
    // Send postMessage callback to wallet (SEP-24)
    // Debugged sendPostMessage and sendCallback logic
    var sendPostMessage = function (transaction) {
        try {
            // SEP-24: payload is the transaction object itself (postMessage uses
            // structured cloning — do NOT JSON.stringify, wallets read event.data
            // directly). Wrap as { transaction: {...} } to match backend HTTPS payload.
            var message = transaction;
            var targetOrigin = "*";
            if (document.referrer) {
                try {
                    targetOrigin = new URL(document.referrer).origin;
                }
                catch (e) {
                    // Fallback to wildcard when referrer origin cannot be parsed
                }
            }
            // SEP-24: Try opener, then parent, fallback to window itself
            // But don't try to postMessage to self if not embedded/opener
            var sent = false;
            if (window.opener && window.opener !== window) {
                window.opener.postMessage(message, targetOrigin);
                sent = true;
            }
            // If no opener or opener is closed, and we are in iframe/embedded, use parent
            else if (window.parent && window.parent !== window) {
                window.parent.postMessage(message, targetOrigin);
                sent = true;
            }
            // Should not postMessage to self in main window context (no effect)
            if (!sent) {
                // Uncomment this if you want the fallback (but may be unnecessary)
                // window.postMessage(message, "*");
            }
        }
        catch (e) {
            console.error("sendPostMessage failed:", e);
        }
    };
    var getCallbackParam = function () {
        var _a;
        var transactionId = (_a = txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction) === null || _a === void 0 ? void 0 : _a.id;
        var storedCallback = readSep24Callbacks({
            transactionId: transactionId,
            sessionToken: effectiveSessionToken,
            authToken: token || null,
        }).callback;
        try {
            var currentParams = new URLSearchParams(location.search);
            var initialParams = new URLSearchParams(window.location.search);
            return (currentParams.get("callback") ||
                initialParams.get("callback") ||
                storedCallback ||
                null);
        }
        catch (e) {
            try {
                var raw = window.location.search;
                if (raw.includes("callback=")) {
                    return new URLSearchParams(raw).get("callback") || storedCallback;
                }
            }
            catch (err) {
                // Ignore parsing errors
            }
        }
        return storedCallback || null;
    };
    var getOnChangeCallbackParam = function () {
        var _a;
        var transactionId = (_a = txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction) === null || _a === void 0 ? void 0 : _a.id;
        var storedOnChange = readSep24Callbacks({
            transactionId: transactionId,
            sessionToken: effectiveSessionToken,
            authToken: token || null,
        }).onChangeCallback;
        try {
            var currentParams = new URLSearchParams(location.search);
            var initialParams = new URLSearchParams(window.location.search);
            return (currentParams.get("on_change_callback") ||
                initialParams.get("on_change_callback") ||
                storedOnChange ||
                null);
        }
        catch (_b) {
            return storedOnChange || null;
        }
    };
    // SEP-24 on_change_callback: fire on every status change.
    // - HTTPS URL: backend already pushes; FE does nothing here.
    // - "postMessage": FE owns the dispatch (backend has no browser context).
    var notifyOnChange = function (nextTxn) {
        var _a, _b;
        var status = (_a = nextTxn === null || nextTxn === void 0 ? void 0 : nextTxn.transaction) === null || _a === void 0 ? void 0 : _a.status;
        var txId = (_b = nextTxn === null || nextTxn === void 0 ? void 0 : nextTxn.transaction) === null || _b === void 0 ? void 0 : _b.id;
        if (!status)
            return;
        // Resolve callback BEFORE marking dedupe — if lookup is transiently empty,
        // we shouldn't burn the slot for this status.
        var onChange = getOnChangeCallbackParam();
        if (onChange !== "postMessage")
            return;
        // Dedupe key includes transaction id so a new transaction with the same
        // initial status as a previous one's terminal status still fires.
        var key = "".concat(txId !== null && txId !== void 0 ? txId : "", ":").concat(status);
        if (lastNotifiedStatusKeyRef.current === key)
            return;
        sendPostMessage(nextTxn);
        capture(ROZO_EVENTS.WALLET_CALLBACK_SENT, {
            callback_type: "postMessage",
            transaction_status: status,
        });
        lastNotifiedStatusKeyRef.current = key;
    };
    var isHttpCallback = function (callback) {
        return callback.startsWith("http://") || callback.startsWith("https://");
    };
    var isDeepLinkCallback = function (callback) {
        return /^[a-z][a-z0-9+.-]*:\/\//i.test(callback);
    };
    var isLobstrWebView = function () {
        return /lobstr/i.test(window.navigator.userAgent);
    };
    var isPostMessageCallback = function (callback) {
        return callback === "postMessage";
    };
    var getWalletReturnUrl = function () {
        var callback = getCallbackParam();
        if (callback &&
            isDeepLinkCallback(callback) &&
            !isHttpCallback(callback) &&
            callback !== "postMessage") {
            return callback;
        }
        if (isLobstrWebView()) {
            return "https://lobstr.co";
        }
        return null;
    };
    var openWalletReturnUrl = function () {
        setShowCloseMessage(false);
    };
    // Send callback to wallet (POST URL or postMessage per SEP-24)
    var sendCallback = function (transaction) { return __awaiter(void 0, void 0, void 0, function () {
        var callback, txStatus, response, _a, _b, _c, error_2;
        var _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    callback = getCallbackParam();
                    if (!callback) {
                        console.warn("No callback found in URL params.");
                        return [2 /*return*/];
                    }
                    txStatus = (_d = transaction === null || transaction === void 0 ? void 0 : transaction.transaction) === null || _d === void 0 ? void 0 : _d.status;
                    if (!(typeof callback === "string" && isHttpCallback(callback))) return [3 /*break*/, 7];
                    _e.label = 1;
                case 1:
                    _e.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, fetch(callback, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(transaction),
                        })];
                case 2:
                    response = _e.sent();
                    if (!!response.ok) return [3 /*break*/, 4];
                    _b = (_a = console).warn;
                    _c = ["Callback POST returned status ".concat(response.status)];
                    return [4 /*yield*/, response.text()];
                case 3:
                    _b.apply(_a, _c.concat([_e.sent()]));
                    _e.label = 4;
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_2 = _e.sent();
                    console.warn("Failed to send callback to URL:", error_2);
                    return [3 /*break*/, 6];
                case 6:
                    capture(ROZO_EVENTS.WALLET_CALLBACK_SENT, {
                        callback_type: "url",
                        transaction_status: txStatus,
                    });
                    return [2 /*return*/];
                case 7:
                    if (isDeepLinkCallback(callback) &&
                        !isHttpCallback(callback) &&
                        !isPostMessageCallback(callback)) {
                        setWalletReturnUrl(callback);
                        capture(ROZO_EVENTS.WALLET_CALLBACK_SENT, {
                            callback_type: "deeplink",
                            transaction_status: txStatus,
                        });
                        return [2 /*return*/];
                    }
                    // If callback parameter is 'postMessage' or any other value, try postMessage
                    sendPostMessage(transaction);
                    capture(ROZO_EVENTS.WALLET_CALLBACK_SENT, {
                        callback_type: "postMessage",
                        transaction_status: txStatus,
                    });
                    return [2 /*return*/];
            }
        });
    }); };
    var handleFinishTransaction = function (sToken, closeWindow) { return __awaiter(void 0, void 0, void 0, function () {
        var txnInfo_2, status, returnUrl, error_3;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!closeWindow) return [3 /*break*/, 6];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 5, , 6]);
                    return [4 /*yield*/, fetchTransaction(sToken)];
                case 2:
                    txnInfo_2 = _b.sent();
                    status = (_a = txnInfo_2 === null || txnInfo_2 === void 0 ? void 0 : txnInfo_2.transaction) === null || _a === void 0 ? void 0 : _a.status;
                    if (!(txnInfo_2 === null || txnInfo_2 === void 0 ? void 0 : txnInfo_2.transaction) || status === "incomplete") {
                        throw new Error("Withdrawal request not ready yet. Please try again.");
                    }
                    if (!(txnInfo_2 === null || txnInfo_2 === void 0 ? void 0 : txnInfo_2.transaction)) return [3 /*break*/, 4];
                    setTxnInfo(txnInfo_2);
                    // Transaction fetched successfully, send callback to wallet
                    return [4 /*yield*/, sendCallback(txnInfo_2)];
                case 3:
                    // Transaction fetched successfully, send callback to wallet
                    _b.sent();
                    returnUrl = getWalletReturnUrl();
                    setWalletReturnUrl(returnUrl);
                    setShowCloseMessage(Boolean(returnUrl));
                    navigate({ pathname: "/status", search: "?session_token=".concat(sToken) }, { replace: true });
                    return [2 /*return*/];
                case 4: return [3 /*break*/, 6];
                case 5:
                    error_3 = _b.sent();
                    console.warn("Failed to finish transaction:", error_3);
                    throw error_3;
                case 6:
                    navigate({ pathname: "/status", search: "?session_token=".concat(sToken) }, { replace: true });
                    return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(_Fragment, { children: [_jsx(Layout.Content, { children: _jsxs(Layout.Inset, { children: [_jsx("div", { style: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: "0.5rem",
                                marginBottom: "0.5rem",
                            }, children: _jsxs("div", { style: {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                }, children: [_jsx(TextLink, { href: "https://bridge.rozo.ai", target: "_blank", rel: "noopener noreferrer", style: {
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }, children: _jsx("img", { src: rozoLogo, alt: "Rozo", height: 40 }) }), _jsx("span", { style: {
                                            fontSize: "1.2rem",
                                            fontWeight: "bold",
                                            color: "var(--pal-text-primary)",
                                            lineHeight: "1.3rem",
                                            whiteSpace: "nowrap",
                                        }, children: headerTitle || "ROZO" })] }) }), showCloseMessage && (_jsxs("div", { style: {
                                position: "fixed",
                                bottom: "16px",
                                left: "16px",
                                right: "16px",
                                zIndex: 9999,
                                backgroundColor: "#ffffff",
                                color: "#0a0a0a",
                                padding: "16px",
                                borderRadius: "4px",
                                border: "1px solid #0a0a0a",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                                animation: "slideUp 0.3s ease-out",
                            }, children: [_jsx("div", { style: {
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: "12px",
                                    }, children: _jsxs("div", { style: { flex: 1 }, children: [_jsx("strong", { style: {
                                                    display: "block",
                                                    marginBottom: "8px",
                                                    fontSize: "1rem",
                                                }, children: "Withdrawal Request Submitted" }), _jsx("span", { style: {
                                                    display: "block",
                                                    fontSize: "0.875rem",
                                                    lineHeight: "1.4",
                                                }, children: "Return to ".concat(isLobstrWebView() ? "Lobstr" : "your wallet", " to review and confirm the withdrawal.") })] }) }), _jsx("div", { style: { marginTop: "12px" }, children: _jsx(Button, { onClick: openWalletReturnUrl, children: "Confirm Now" }) })] })), bootstrapError && !location.pathname.includes("txn") && (_jsx("div", { className: "InfoBlock InfoBlock--error", children: bootstrapError })), _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Welcome, {}) }), _jsx(Route, { path: "/start", element: _jsx(Navigate, { to: { pathname: "/", search: location.search }, replace: true }) }), _jsx(Route, { path: "/kyc", element: _jsx(ProtectedRoute, { hasSessionToken: Boolean(effectiveSessionToken), children: _jsx(Kyc, { account: (txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction)
                                                ? getUserAccount(txnInfo.transaction)
                                                : "", type: (_b = txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction) === null || _b === void 0 ? void 0 : _b.kind, withdrawAmount: (txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction)
                                                ? getRequestedAmount(txnInfo.transaction)
                                                : undefined, initialFeeMode: (txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction)
                                                ? getFeeMode(txnInfo.transaction)
                                                : undefined, isFeeModeLocked: (txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction)
                                                ? getIsFeeModeLocked(txnInfo.transaction)
                                                : false, assetCode: (txnInfo === null || txnInfo === void 0 ? void 0 : txnInfo.transaction)
                                                ? getAmountInAsset(txnInfo.transaction)
                                                : "USDC", sessionToken: effectiveSessionToken, callback: handleFinishTransaction }) }) }), _jsx(Route, { path: "/status", element: _jsx(ProtectedRoute, { hasSessionToken: Boolean(effectiveSessionToken), children: _jsx(Status, { txnInfo: txnInfo, sessionToken: effectiveSessionToken, onTxnInfoUpdate: function (nextTxn) {
                                                setTxnInfo(nextTxn);
                                                notifyOnChange(nextTxn);
                                            } }) }) }), _jsx(Route, { path: "/txn", element: _jsx(TxnInfo, { txnId: (_c = searchParams.get("transaction_id")) !== null && _c !== void 0 ? _c : "", token: (_d = searchParams.get("token")) !== null && _d !== void 0 ? _d : "", onTxnInfoUpdate: function (transaction) {
                                            var nextTxn = { transaction: transaction };
                                            setTxnInfo(nextTxn);
                                            notifyOnChange(nextTxn);
                                        } }) }), _jsx(Route, { path: "*", element: _jsx(NotFound, {}) })] })] }) }), _jsx(Layout.Footer, { hideLegalLinks: true, children: _jsxs("div", { className: "Footer__links", children: [location.pathname === "/" && _jsx(Partners, {}), _jsx(TextLink, { href: "https://bridge.rozo.ai/faq", target: "_blank", rel: "noopener noreferrer", style: { color: "var(--pal-text-secondary)" }, children: "FAQ" }), _jsx(TextLink, { href: "https://bridge.rozo.ai/terms", target: "_blank", rel: "noopener noreferrer", style: { color: "var(--pal-text-secondary)" }, children: "Terms of Use" }), _jsx(TextLink, { href: "https://bridge.rozo.ai/privacy", target: "_blank", rel: "noopener noreferrer", style: { color: "var(--pal-text-secondary)" }, children: "Privacy Policy" })] }) })] }));
};
